// src/pages/Registrations.js
//
// CORRECTIONS :
//  • courseTitle affiche maintenant TOUS les cours (selectedCourses[].title)
//    quand il y en a plusieurs.
//  • La carte 10 cours affiche sa barre de progression et le nombre de
//    consommations (creditsUsed / 10).
//  • isPaid unifié en fonction helper (gère paid / payé / paye).
//  • Colonne "Source" distingue App / Site / Web.
//  • Dialog détail : affiche aussi l'historique consumedLessons.
//
// SUIVI DES ABONNEMENTS (v5 — affichage seul, aucune écriture supplémentaire) :
//  • Colonne « Crédits » devenue « Suivi » : pour un abonnement récurrent Mollie,
//    affiche statut, mensualités payées / total et prochaine échéance
//    (champs déjà écrits par createPayment / mollieWebhook : subscriptionStatus,
//    installmentsPaid, installmentsTotal, nextPaymentDate).
//  • Nouveau filtre « Suivi » : abonnements, en retard, prélèvement échoué,
//    échéance ce mois-ci, cartes 10 presque épuisées.
//  • Dialog détail : section « Abonnement » + historique des paiements en ligne
//    (collection payments, filtrée sur refPath = registrations/{id}).
//  • Export CSV enrichi (statut, mensualités, prochaine échéance, début).

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, where, doc, setDoc, deleteDoc } from 'firebase/firestore';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, IconButton, Tooltip,
  CircularProgress, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, FormControl, InputLabel, Grid,
  LinearProgress, Snackbar, Alert,
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, FileDownload as DownloadIcon,
  Visibility as ViewIcon, PhoneAndroid as AppIcon, Delete as DeleteIcon,
  Videocam as ImageYesIcon, VideocamOff as ImageNoIcon, HelpOutline as ImageUnknownIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import moment from 'moment';

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────

const readDate = (date) => {
  if (!date) return null;
  if (date?.toDate) return date.toDate();
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (date) => {
  const d = readDate(date);
  return d ? moment(d).format('DD/MM/YYYY HH:mm') : 'N/A';
};

const money = (value) => {
  const n = Number(value || 0);
  return `${n.toFixed(Number.isInteger(n) ? 0 : 2)} €`;
};

const isPaid = (status) =>
  ['paid', 'payé', 'paye'].includes((status || '').toLowerCase());

/** Style de la puce "Paiement" du tableau des inscriptions (reg.paymentStatus) */
const paymentStatusMeta = (status) =>
  isPaid(status)
    ? { label: 'Payé',    bg: '#e8f5e9', color: '#2e7d32' }
    : { label: 'À payer', bg: '#fff3e0', color: '#e65100' };

/**
 * Date « jour » : gère les chaînes ISO 'YYYY-MM-DD' (nextPaymentDate, écrite
 * par le webhook) en heure LOCALE — new Date('2026-11-05') serait interprétée
 * en UTC et pourrait tomber la veille — ainsi que les Timestamp Firestore.
 */
const readDay = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  return readDate(value);
};

const formatDay = (value) => {
  const d = readDay(value);
  return d ? moment(d).format('DD/MM/YYYY') : '—';
};

// Délai de grâce : un prélèvement SEPA peut rester « pending » quelques jours
// avant d'être confirmé — on ne parle de retard qu'au-delà.
const OVERDUE_GRACE_DAYS = 5;

// Statuts écrits par createPayment / mollieWebhook / chargeRecurringInstallments
const SUB_STATUS = {
  pending_mandate: { label: 'Mandat en attente',  bg: '#fff3e0', color: '#e65100' },
  active:          { label: 'Actif',              bg: '#e8f5e9', color: '#2e7d32' },
  payment_failed:  { label: 'Prélèvement échoué', bg: '#ffebee', color: '#c62828' },
  mandate_failed:  { label: 'Mandat refusé',      bg: '#ffebee', color: '#c62828' },
  completed:       { label: 'Terminé',            bg: '#eceff1', color: '#455a64' },
};
const SUB_OVERDUE = { label: 'En retard', bg: '#ffebee', color: '#c62828' };

const FREQUENCY_LABELS = {
  monthly:   'Mensuel (domiciliation SEPA)',
  semester:  'Semestriel',
  semester1: 'Semestre 1',
  semester2: 'Semestre 2',
  annual:    'Annuel',
};

/**
 * Résumé du suivi d'abonnement récurrent, calculé une fois au chargement.
 * isSubscription = abonnement/formule avec échéances (installmentsTotal > 0
 * ou statut d'abonnement présent). Les paiements uniques (annuel, essai,
 * carte 10) ne sont pas concernés.
 */
const getSubscriptionInfo = (d) => {
  const total = Number(d.installmentsTotal || 0);
  const paid  = Number(d.installmentsPaid  || 0);
  const status = d.subscriptionStatus || null;
  const isSubscription = total > 0 || (status && SUB_STATUS[status] !== undefined);
  if (!isSubscription) return { isSubscription: false };

  const next = readDay(d.nextPaymentDate);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const limit = new Date(today); limit.setDate(limit.getDate() - OVERDUE_GRACE_DAYS);
  const overdue = status === 'active' && next !== null && next < limit;

  const meta = overdue ? SUB_OVERDUE : (SUB_STATUS[status] || { label: status || '—', bg: '#f5f5f5', color: '#616161' });
  const now = new Date();
  const dueThisMonth = status === 'active' && next !== null
    && next.getFullYear() === now.getFullYear() && next.getMonth() === now.getMonth();

  return {
    isSubscription: true,
    total, paid, next, status, overdue, dueThisMonth,
    failed: status === 'payment_failed' || status === 'mandate_failed',
    label: meta.label, bg: meta.bg, color: meta.color,
  };
};

const SubscriptionStatusChip = ({ sub }) => (
  <Chip label={sub.label} size="small" sx={{ backgroundColor: sub.bg, color: sub.color }} />
);

// Statuts des documents de la collection payments (Mollie)
const PAYMENT_DOC_STATUS = {
  paid:     { label: 'Payé',     bg: '#e8f5e9', color: '#2e7d32' },
  open:     { label: 'En cours', bg: '#fff3e0', color: '#e65100' },
  pending:  { label: 'En cours', bg: '#fff3e0', color: '#e65100' },
  failed:   { label: 'Échoué',   bg: '#ffebee', color: '#c62828' },
  expired:  { label: 'Expiré',   bg: '#eceff1', color: '#455a64' },
  canceled: { label: 'Annulé',   bg: '#eceff1', color: '#455a64' },
};

const METHOD_LABELS = {
  directdebit: 'Prélèvement SEPA',
  bancontact:  'Bancontact',
  creditcard:  'Carte bancaire',
};

/** Construit le label de la colonne "Cours" depuis un document registration */
const buildCourseLabel = (data) => {
  if (Array.isArray(data.selectedCourses) && data.selectedCourses.length > 0) {
    return data.selectedCourses.map((c) => c.title || c.id || '').filter(Boolean).join(', ');
  }
  return data.courseTitle || data.courseId || 'N/A';
};

/** Source chip */
const SourceChip = ({ source }) => {
  if (source === 'mobile_app') return <Chip icon={<AppIcon />} label="App"  size="small" variant="outlined" />;
  return <Chip label="Site" size="small" variant="outlined" />;
};

/**
 * Droit à l'image : source de vérité = persons[].imageConsent (par personne).
 * Fallback sur les anciens champs (imageRightsConsentAll / imageRightsConsent)
 * pour les inscriptions créées avant le passage au consentement par enfant.
 */
const getPersonImageConsent = (person, data) => {
  if (person && person.imageConsent !== undefined && person.imageConsent !== null) {
    return person.imageConsent;
  }
  if (data.imageRightsConsentAll !== undefined) return data.imageRightsConsentAll;
  if (data.imageRightsConsent !== undefined) return data.imageRightsConsent;
  return null;
};

/** Résumé "Image" pour une inscription : all / none / mixed / unknown */
const summarizeImageConsent = (persons, data) => {
  if (!persons || persons.length === 0) {
    const legacy = getPersonImageConsent(null, data);
    return legacy === null ? 'unknown' : legacy ? 'all' : 'none';
  }
  const values = persons.map((p) => getPersonImageConsent(p, data));
  if (values.some((v) => v === null)) return 'unknown';
  if (values.every((v) => v === true)) return 'all';
  if (values.every((v) => v === false)) return 'none';
  return 'mixed';
};

/** Chip "Image" (résumé par inscription) */
const ImageConsentChip = ({ status }) => {
  if (status === 'all') {
    return <Chip icon={<ImageYesIcon fontSize="small" />} label="Oui" size="small"
      sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />;
  }
  if (status === 'none') {
    return <Chip icon={<ImageNoIcon fontSize="small" />} label="Non" size="small"
      sx={{ backgroundColor: '#ffebee', color: '#c62828' }} />;
  }
  if (status === 'mixed') {
    return <Chip icon={<ImageYesIcon fontSize="small" />} label="Mixte" size="small"
      sx={{ backgroundColor: '#fff3e0', color: '#e65100' }} />;
  }
  return <Chip icon={<ImageUnknownIcon fontSize="small" />} label="Non renseigné" size="small" variant="outlined" />;
};

// ──────────────────────────────────────────────────────────────────────────────
// Composant principal
// ──────────────────────────────────────────────────────────────────────────────

const Registrations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [registrations,        setRegistrations]        = useState([]);
  const [loading,              setLoading]              = useState(true);
  const [searchTerm,           setSearchTerm]           = useState('');
  const [paymentFilter,        setPaymentFilter]        = useState('all');
  const [followFilter,         setFollowFilter]         = useState('all');
  const [page,                 setPage]                 = useState(0);
  const [rowsPerPage,          setRowsPerPage]          = useState(10);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [dialogOpen,           setDialogOpen]           = useState(false);
  // Paiements en ligne (collection payments) de l'inscription affichée
  const [regPayments,          setRegPayments]          = useState([]);
  const [regPaymentsLoading,   setRegPaymentsLoading]   = useState(false);
  const [snackbar,             setSnackbar]             = useState({ open: false, message: '', severity: 'success' });
  // Ajout manuel d'une présence sur une carte 10 (formulaire du dialog détail)
  const [newConsumptionDate,   setNewConsumptionDate]   = useState('');
  const [newConsumptionCourse, setNewConsumptionCourse] = useState('');
  // Dialog ouvert depuis Subscriptions.js : "Fermer" doit y ramener plutôt
  // que de laisser l'admin planté sur la liste générale des inscriptions.
  const [cameFromSubscriptions, setCameFromSubscriptions] = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const q    = query(collection(db, 'registrations'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);

      const data = snap.docs.map((docSnap) => {
        const d       = docSnap.data();
        const persons = Array.isArray(d.persons) ? d.persons : [];
        const payment = d.paymentDetails || {};

        // Noms participants
        const personsInfo = persons
          .map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim())
          .filter(Boolean)
          .join(', ') || d.targetStudentName || '';

        // Tous les cours (multi-cours)
        const coursesLabel = buildCourseLabel(d);

        // consumedLessons pour carte10
        const consumedLessons = Array.isArray(d.consumedLessons) ? d.consumedLessons : [];

        return {
          id: docSnap.id,
          ...d,
          personsCount:   persons.length,
          personsInfo,
          coursesLabel,
          contactDisplay: `${d.contactInfo?.email || ''}${d.contactInfo?.phone ? ` (${d.contactInfo.phone})` : ''}`,
          parentContact:  d.parentInfo
            ? `${d.parentInfo.parentFirstName || ''} ${d.parentInfo.parentLastName || ''}`.trim()
            : '',
          type:           d.registrationType === 'child' ? 'Enfant' : 'Adulte',
          amount:         Number(payment.totalAmount || payment.amount || 0),
          paymentMethod:  payment.paymentMethod || '',
          creditsTotal:   Number(d.creditsTotal   || d.includedCourses || d.courseCredits || 0),
          creditsUsed:    Number(d.creditsUsed    || consumedLessons.length || 0),
          creditsRemaining: Number(d.creditsRemaining || 0),
          paymentStatus:  d.paymentStatus || 'pending',
          source:         d.source || 'site',
          consumedLessons,
          imageConsentStatus: summarizeImageConsent(persons, d),
          sub:            getSubscriptionInfo(d),
        };
      });

      setRegistrations(data);
    } catch (e) {
      console.error('Erreur chargement inscriptions:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRegistrations(); }, []);

  // Ouverture directe d'une inscription (arrivée depuis Subscriptions.js, qui
  // navigue ici avec l'id dans le state) — une fois les inscriptions chargées.
  useEffect(() => {
    const targetId = location.state?.openRegistrationId;
    if (!targetId || registrations.length === 0) return;
    const target = registrations.find((r) => r.id === targetId);
    if (target) {
      handleViewDetails(target);
      setCameFromSubscriptions(true);
      // On efface le state de navigation pour ne pas rouvrir le dialog si
      // l'admin ferme puis revient en arrière sur cette page.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrations, location.state]);

  // ── Paiements en ligne de l'inscription affichée ──────────────────────────
  // Les documents payments sont écrits par les Cloud Functions (un par
  // paiement Mollie : 1re échéance, échéances récurrentes, paiement unique).
  // La lecture (list) est autorisée au staff par les rules.
  const selectedId = selectedRegistration?.id;
  useEffect(() => {
    if (!dialogOpen || !selectedId) { setRegPayments([]); return undefined; }
    let cancelled = false;
    (async () => {
      setRegPaymentsLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, 'payments'), where('refPath', '==', `registrations/${selectedId}`))
        );
        const rows = snap.docs.map((p) => ({ id: p.id, ...p.data() }));
        rows.sort((a, b) => {
          const na = a.installmentNumber ?? 999;
          const nb = b.installmentNumber ?? 999;
          if (na !== nb) return na - nb;
          return (readDate(a.createdAt)?.getTime() || 0) - (readDate(b.createdAt)?.getTime() || 0);
        });
        if (!cancelled) setRegPayments(rows);
      } catch (e) {
        console.error('Erreur chargement paiements de l\'inscription:', e);
        if (!cancelled) setRegPayments([]);
      } finally {
        if (!cancelled) setRegPaymentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dialogOpen, selectedId]);

  // ── Update payment ─────────────────────────────────────────────────────────

  const updatePaymentStatus = async (registration, status) => {
    await setDoc(doc(db, 'registrations', registration.id), {
      paymentStatus:     status,
      paymentUpdatedAt:  new Date(),
      updatedAt:         new Date(),
    }, { merge: true });
    await fetchRegistrations();
    setSelectedRegistration((prev) => prev ? { ...prev, paymentStatus: status } : prev);
  };

  const handleViewDetails = (reg) => {
    setSelectedRegistration(reg);
    setDialogOpen(true);
    setNewConsumptionDate('');
    setNewConsumptionCourse('');
  };

  // Fermeture du dialog détail : si on y est arrivé depuis Subscriptions.js,
  // on y retourne directement plutôt que de laisser l'admin sur la liste
  // générale des inscriptions sans repère pour revenir en arrière.
  const closeDialog = () => {
    setDialogOpen(false);
    if (cameFromSubscriptions) {
      setCameFromSubscriptions(false);
      navigate('/subscriptions');
    }
  };

  // ── Carte 10 cours : présence ajoutée/retirée à la main ──────────────────
  // Utile quand un élève est venu sans réserver dans l'app, ou pour corriger
  // une erreur — écrit directement consumedLessons + les deux compteurs
  // (creditsUsed/creditsRemaining), sur le même modèle que la consommation
  // automatique côté app.
  const writeConsumedLessons = async (reg, nextConsumed) => {
    const creditsUsed = nextConsumed.length;
    const creditsRemaining = Math.max((reg.creditsTotal || 0) - creditsUsed, 0);
    await setDoc(doc(db, 'registrations', reg.id), {
      consumedLessons: nextConsumed,
      creditsUsed,
      creditsRemaining,
      updatedAt: new Date(),
    }, { merge: true });
    await fetchRegistrations();
    setSelectedRegistration((prev) => (prev && prev.id === reg.id)
      ? { ...prev, consumedLessons: nextConsumed, creditsUsed, creditsRemaining }
      : prev);
  };

  const handleAddConsumption = async (reg) => {
    if (!newConsumptionDate) return;
    if (reg.creditsTotal > 0 && reg.creditsUsed >= reg.creditsTotal) {
      setSnackbar({ open: true, message: 'Cette carte a déjà atteint son nombre de crédits.', severity: 'error' });
      return;
    }
    const course = (reg.selectedCourses || []).find((c) => c.id === newConsumptionCourse);
    const entry = {
      dateKey: newConsumptionDate,
      courseId: course?.id || newConsumptionCourse || null,
      courseTitle: course?.title || reg.courseTitle || null,
      source: 'admin',
    };
    await writeConsumedLessons(reg, [...(reg.consumedLessons || []), entry]);
    setNewConsumptionDate('');
  };

  const handleRemoveConsumption = async (reg, index) => {
    if (!window.confirm('Retirer cette présence de la carte 10 (le crédit sera recrédité) ?')) return;
    const nextConsumed = (reg.consumedLessons || []).filter((_, i) => i !== index);
    await writeConsumedLessons(reg, nextConsumed);
  };

  // ── Suppression ────────────────────────────────────────────────────────────

  const handleDeleteRegistration = async (reg) => {
    const label = reg.personsInfo || reg.targetStudentName || reg.contactDisplay || 'cette inscription';
    if (!window.confirm(`Supprimer l'inscription de "${label}" ? Cette action est irréversible.`)) return;

    try {
      await deleteDoc(doc(db, 'registrations', reg.id));

      setRegistrations((prev) => prev.filter((r) => r.id !== reg.id));

      // Fermer le dialog si l'inscription supprimée était affichée
      if (selectedRegistration?.id === reg.id) {
        closeDialog();
        setSelectedRegistration(null);
      }

      setSnackbar({ open: true, message: 'Inscription supprimée', severity: 'success' });
    } catch (e) {
      console.error('Erreur lors de la suppression de l\'inscription:', e);
      setSnackbar({ open: true, message: "Erreur lors de la suppression de l'inscription", severity: 'error' });
    }
  };

  // ── Filtres ────────────────────────────────────────────────────────────────

  const filteredData = registrations.filter((reg) => {
    const haystack = [
      reg.planName, reg.plan, reg.coursesLabel, reg.contactDisplay,
      reg.personsInfo, reg.parentContact, reg.targetStudentName, reg.source,
    ].join(' ').toLowerCase();
    const searchOk  = !searchTerm || haystack.includes(searchTerm.toLowerCase());
    const paymentOk = paymentFilter === 'all'
      || (paymentFilter === 'paid'    ?  isPaid(reg.paymentStatus)
      :                                 !isPaid(reg.paymentStatus));
    const sub = reg.sub || {};
    const followOk = followFilter === 'all'
      || (followFilter === 'subscriptions' ? sub.isSubscription === true
      :  followFilter === 'overdue'        ? sub.overdue === true
      :  followFilter === 'failed'         ? sub.failed === true
      :  followFilter === 'dueThisMonth'   ? sub.dueThisMonth === true
      :  followFilter === 'cardLow'        ? (reg.plan === 'carte10' && reg.creditsTotal > 0 && reg.creditsRemaining <= 2)
      :  true);
    return searchOk && paymentOk && followOk;
  });

  // ── CSV ────────────────────────────────────────────────────────────────────

  const csvData = filteredData.map((reg) => ({
    'Date inscription': formatDate(reg.createdAt),
    'Source':           reg.source,
    'Formule':          reg.planName,
    'Cours':            reg.coursesLabel,
    'Personnes':        reg.personsCount,
    'Noms':             reg.personsInfo,
    'Contact':          reg.contactDisplay,
    'Type':             reg.type,
    'Paiement':         isPaid(reg.paymentStatus) ? 'Payé' : 'À payer',
    'Montant':          money(reg.amount),
    'Crédits total':    reg.creditsTotal,
    'Crédits utilisés': reg.creditsUsed,
    'Crédits restants': reg.creditsRemaining,
    'Abonnement - statut':      reg.sub?.isSubscription ? reg.sub.label : '',
    'Abonnement - début':       reg.subscriptionStartDate ? formatDay(reg.subscriptionStartDate) : '',
    'Mensualités payées':       reg.sub?.isSubscription ? reg.sub.paid : '',
    'Mensualités total':        reg.sub?.isSubscription ? reg.sub.total : '',
    'Prochaine échéance':       reg.sub?.isSubscription && reg.sub.next ? formatDay(reg.nextPaymentDate) : '',
    'Droit à l\'image':  { all: 'Oui', none: 'Non', mixed: 'Mixte', unknown: 'Non renseigné' }[reg.imageConsentStatus],
  }));

  // ── Colonnes ───────────────────────────────────────────────────────────────

  const columns = [
    { id: 'actions',       label: 'Actions',       minWidth: 80,  align: 'center' },
    { id: 'createdAt',     label: 'Date',          minWidth: 145 },
    { id: 'source',        label: 'Source',        minWidth: 90  },
    { id: 'planName',      label: 'Formule',       minWidth: 150 },
    { id: 'coursesLabel',  label: 'Cours',         minWidth: 180 },
    { id: 'personsInfo',   label: 'Participant(s)', minWidth: 180 },
    { id: 'amount',        label: 'Paiement',      minWidth: 160 },
    { id: 'tracking',      label: 'Suivi',         minWidth: 190 },
    { id: 'imageConsent',  label: 'Image',         minWidth: 110 },
    { id: 'contactDisplay',label: 'Contact',       minWidth: 220 },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Gestion des inscriptions
      </Typography>

      {/* ── Filtres ──────────────────────────────────────────────────────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth variant="outlined" size="small" placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} /> }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Paiement</InputLabel>
              <Select label="Paiement" value={paymentFilter} onChange={(e) => { setPaymentFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="paid">Payés</MenuItem>
                <MenuItem value="pending">À payer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Suivi</InputLabel>
              <Select label="Suivi" value={followFilter} onChange={(e) => { setFollowFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="subscriptions">Abonnements récurrents</MenuItem>
                <MenuItem value="overdue">En retard</MenuItem>
                <MenuItem value="failed">Prélèvement / mandat échoué</MenuItem>
                <MenuItem value="dueThisMonth">Échéance ce mois-ci</MenuItem>
                <MenuItem value="cardLow">Cartes 10 presque épuisées (≤ 2)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <CSVLink
              data={csvData}
              filename={`inscriptions-${moment().format('YYYYMMDD-HHmm')}.csv`}
              style={{ textDecoration: 'none' }}
            >
              <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ mr: 1 }}>Exporter CSV</Button>
            </CSVLink>
            <Tooltip title="Actualiser">
              <IconButton onClick={fetchRegistrations}><RefreshIcon /></IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Tableau ──────────────────────────────────────────────────────── */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align || 'left'}
                      sx={{ minWidth: col.minWidth, fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((reg) => {
                    const isCard10    = reg.plan === 'carte10';
                    const isUnlimited = reg.creditsTotal >= 999;
                    const progress    = isCard10 && reg.creditsTotal > 0
                      ? (reg.creditsUsed / reg.creditsTotal)
                      : null;

                    return (
                      <TableRow hover key={reg.id}>
                        {/* Actions */}
                        <TableCell align="center">
                          <Tooltip title="Voir détails">
                            <IconButton size="small" onClick={() => handleViewDetails(reg)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer l'inscription">
                            <IconButton size="small" color="error" onClick={() => handleDeleteRegistration(reg)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>

                        {/* Date */}
                        <TableCell>{formatDate(reg.createdAt)}</TableCell>

                        {/* Source */}
                        <TableCell><SourceChip source={reg.source} /></TableCell>

                        {/* Formule */}
                        <TableCell>
                          <Chip
                            label={reg.planName || reg.plan || 'Formule'}
                            color={
                              reg.plan === 'essai'   ? 'primary'   :
                              reg.plan === 'unique'  ? 'secondary' :
                              reg.plan === 'carte10' ? 'warning'   : 'success'
                            }
                            size="small"
                          />
                        </TableCell>

                        {/* Cours (tous les cours de l'inscription) */}
                        <TableCell sx={{ maxWidth: 200 }}>
                          <Typography variant="body2" noWrap title={reg.coursesLabel}>
                            {reg.coursesLabel}
                          </Typography>
                        </TableCell>

                        {/* Participants */}
                        <TableCell>
                          <div>{reg.personsInfo || 'N/A'}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>{reg.type}</div>
                        </TableCell>

                        {/* Paiement */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={paymentStatusMeta(reg.paymentStatus).label}
                              size="small"
                              sx={{
                                backgroundColor: paymentStatusMeta(reg.paymentStatus).bg,
                                color:           paymentStatusMeta(reg.paymentStatus).color,
                              }}
                            />
                            {!isPaid(reg.paymentStatus) && (
                              <Tooltip title="Marquer payé">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); updatePaymentStatus(reg, 'paid'); }}
                                  sx={{ color: '#2e7d32' }}
                                >
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <div style={{ marginTop: 4 }}>{money(reg.amount)}</div>
                          {reg.sub?.isSubscription && Number(reg.installmentAmount) > 0 && (
                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                              puis {money(reg.installmentAmount)} / échéance
                            </div>
                          )}
                        </TableCell>

                        {/* Suivi : abonnement récurrent / crédits carte */}
                        <TableCell sx={{ minWidth: 190 }}>
                          {reg.sub?.isSubscription ? (
                            <>
                              <SubscriptionStatusChip sub={reg.sub} />
                              {reg.sub.total > 0 && (
                                <>
                                  <div style={{ marginTop: 4 }}>
                                    {reg.sub.paid}/{reg.sub.total} échéances payées
                                  </div>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min((reg.sub.paid / reg.sub.total) * 100, 100)}
                                    sx={{ mt: 0.5, height: 6, borderRadius: 4, backgroundColor: '#eee',
                                          '& .MuiLinearProgress-bar': { backgroundColor: '#7C3AED' } }}
                                  />
                                </>
                              )}
                              {reg.sub.next && reg.sub.status !== 'completed' && (
                                <div style={{ fontSize: '0.75rem', color: reg.sub.overdue ? '#c62828' : '#666' }}>
                                  Prochaine échéance : {formatDay(reg.nextPaymentDate)}
                                </div>
                              )}
                              {reg.subscriptionStartDate && (
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  Début : {formatDay(reg.subscriptionStartDate)}
                                </div>
                              )}
                            </>
                          ) : isUnlimited ? (
                            <>
                              <span>Illimité</span>
                              {reg.subscriptionStartDate && (
                                <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                  Depuis le {formatDay(reg.subscriptionStartDate)}
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <span>{reg.creditsUsed}/{reg.creditsTotal}</span>
                              {progress !== null && (
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(progress * 100, 100)}
                                  sx={{ mt: 0.5, height: 6, borderRadius: 4, backgroundColor: '#eee',
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#7C3AED' } }}
                                />
                              )}
                              <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                {reg.creditsRemaining} restants
                              </div>
                            </>
                          )}
                        </TableCell>

                        {/* Image */}
                        <TableCell>
                          <ImageConsentChip status={reg.imageConsentStatus} />
                        </TableCell>

                        {/* Contact */}
                        <TableCell>{reg.contactDisplay}</TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          labelRowsPerPage="Lignes par page :"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      {/* ── Dialog détail ─────────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Détails de l'inscription
          {cameFromSubscriptions && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
              Ouvert depuis Gestion des abonnements — « Fermer » t'y ramène.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRegistration && (() => {
            const reg        = selectedRegistration;
            const isCard10   = reg.plan === 'carte10';
            const isUnlimited= reg.creditsTotal >= 999;
            const consumed   = reg.consumedLessons || [];

            return (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Inscription du {formatDate(reg.createdAt)}
                </Typography>

                <Grid container spacing={2}>
                  {/* Formule + cours */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Formule</Typography>
                    <Typography>{reg.planName || reg.plan}</Typography>
                    <Typography sx={{ mt: 1 }}>
                      <strong>Cours :</strong> {reg.coursesLabel}
                    </Typography>
                    <Typography>
                      <strong>Source :</strong> {reg.source === 'mobile_app' ? 'Application mobile' : 'Site web'}
                    </Typography>
                  </Grid>

                  {/* Paiement */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Paiement</Typography>
                    <Typography>Montant : {money(reg.amount)}</Typography>
                    <Typography>Statut : {isPaid(reg.paymentStatus) ? 'Payé' : 'À payer'}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small" variant="contained"
                        onClick={() => updatePaymentStatus(reg, 'paid')}
                        sx={{ mr: 1, backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
                      >
                        Marquer payé
                      </Button>
                      <Button
                        size="small" variant="outlined"
                        onClick={() => updatePaymentStatus(reg, 'pending')}
                      >
                        Remettre à payer
                      </Button>
                    </Box>
                    {reg.sub?.isSubscription && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Abonnement récurrent : ces boutons ne modifient que le statut global,
                        pas les échéances payées.
                      </Typography>
                    )}
                  </Grid>

                  {/* Abonnement (échéances) */}
                  {(reg.sub?.isSubscription || reg.subscriptionStartDate) && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Abonnement</Typography>
                      {reg.sub?.isSubscription && (
                        <Box sx={{ mb: 1 }}><SubscriptionStatusChip sub={reg.sub} /></Box>
                      )}
                      <Typography>
                        <strong>Fréquence :</strong>{' '}
                        {FREQUENCY_LABELS[reg.paymentFrequency] || reg.paymentFrequency || '—'}
                      </Typography>
                      {reg.subscriptionStartDate && (
                        <Typography>
                          <strong>Date de début :</strong> {formatDay(reg.subscriptionStartDate)}
                        </Typography>
                      )}
                      {Number(reg.installmentAmount) > 0 && (
                        <Typography>
                          <strong>Montant par échéance :</strong> {money(reg.installmentAmount)}
                        </Typography>
                      )}
                      {reg.sub?.isSubscription && reg.sub.total > 0 && (
                        <>
                          <Typography>
                            <strong>Échéances payées :</strong> {reg.sub.paid} / {reg.sub.total}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min((reg.sub.paid / reg.sub.total) * 100, 100)}
                            sx={{ mt: 0.5, mb: 0.5, height: 8, borderRadius: 4, backgroundColor: '#eee',
                                  '& .MuiLinearProgress-bar': { backgroundColor: '#7C3AED' } }}
                          />
                        </>
                      )}
                      {reg.sub?.next && reg.sub.status !== 'completed' && (
                        <Typography sx={{ color: reg.sub.overdue ? '#c62828' : 'inherit' }}>
                          <strong>Prochaine échéance :</strong> {formatDay(reg.nextPaymentDate)}
                          {reg.sub.overdue ? ' (en retard)' : ''}
                        </Typography>
                      )}
                      {reg.sub?.isSubscription && (
                        <Typography>
                          <strong>Mandat SEPA :</strong> {reg.mollieMandateId ? 'Actif' : 'Aucun'}
                        </Typography>
                      )}
                      {reg.chargeInProgress && (
                        <Typography variant="caption" color="text.secondary">
                          Un prélèvement est en cours de traitement.
                        </Typography>
                      )}
                    </Grid>
                  )}

                  {/* Paiements en ligne (collection payments) */}
                  {(regPaymentsLoading || regPayments.length > 0) && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Paiements en ligne</Typography>
                      {regPaymentsLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <TableContainer sx={{ maxHeight: 260 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell>Échéance</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Montant</TableCell>
                                <TableCell>Méthode</TableCell>
                                <TableCell>Statut</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {regPayments.map((p) => {
                                const st = PAYMENT_DOC_STATUS[p.status] || { label: p.status || '—', bg: '#f5f5f5', color: '#616161' };
                                return (
                                  <TableRow key={p.id}>
                                    <TableCell>
                                      {p.installmentNumber
                                        ? `${p.installmentNumber}/${p.installmentsTotal || '?'}`
                                        : 'Paiement unique'}
                                    </TableCell>
                                    <TableCell>{formatDate(p.paidAt || p.createdAt)}</TableCell>
                                    <TableCell>{money(p.amountGross)}</TableCell>
                                    <TableCell>{METHOD_LABELS[p.method] || p.method || '—'}</TableCell>
                                    <TableCell>
                                      <Chip label={st.label} size="small" sx={{ backgroundColor: st.bg, color: st.color }} />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Grid>
                  )}

                  {/* Participants */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Participant(s)</Typography>
                    {Array.isArray(reg.persons) && reg.persons.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {reg.persons.map((p, i) => {
                          const consent = getPersonImageConsent(p, reg);
                          return (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ minWidth: 200 }}>
                                {p.firstName} {p.lastName}
                              </Typography>
                              <ImageConsentChip status={consent === null ? 'unknown' : consent ? 'all' : 'none'} />
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography>{reg.personsInfo || reg.targetStudentName || 'N/A'}</Typography>
                    )}
                  </Grid>

                  {/* Crédits */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Suivi des crédits</Typography>
                    {isUnlimited ? (
                      <Typography>Formule illimitée</Typography>
                    ) : (
                      <>
                        <Typography>
                          {reg.creditsUsed} utilisé(s) / {reg.creditsTotal} — {reg.creditsRemaining} restant(s)
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min((reg.creditsUsed / Math.max(reg.creditsTotal, 1)) * 100, 100)}
                          sx={{ mt: 1, height: 8, borderRadius: 4, backgroundColor: '#eee',
                                '& .MuiLinearProgress-bar': { backgroundColor: '#7C3AED' } }}
                        />
                      </>
                    )}
                  </Grid>

                  {/* Historique consommations (carte10) — toujours affiché pour
                      une carte 10, avec le formulaire d'ajout manuel */}
                  {isCard10 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Historique des consommations (carte 10)
                      </Typography>
                      {consumed.length > 0 ? (
                        <Box sx={{ maxHeight: 220, overflow: 'auto', mb: 2 }}>
                          {consumed.map((cl, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                              <Chip label={cl.dateKey} size="small" />
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {cl.courseTitle || cl.courseId || '—'}
                              </Typography>
                              {cl.source && (
                                <Typography variant="caption" color="text.secondary">({cl.source})</Typography>
                              )}
                              <IconButton size="small" onClick={() => handleRemoveConsumption(reg, i)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Aucune présence enregistrée pour l'instant.
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Ajouter une présence (élève venu sans réserver dans l'app, ou correction) :
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                          type="date"
                          size="small"
                          label="Date"
                          InputLabelProps={{ shrink: true }}
                          value={newConsumptionDate}
                          onChange={(e) => setNewConsumptionDate(e.target.value)}
                        />
                        {(reg.selectedCourses || []).length > 1 ? (
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Cours</InputLabel>
                            <Select
                              label="Cours"
                              value={newConsumptionCourse}
                              onChange={(e) => setNewConsumptionCourse(e.target.value)}
                            >
                              {reg.selectedCourses.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {reg.coursesLabel || 'Cours de la carte'}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!newConsumptionDate}
                          onClick={() => handleAddConsumption(reg)}
                          sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
                        >
                          Ajouter
                        </Button>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => selectedRegistration && handleDeleteRegistration(selectedRegistration)}
            sx={{ mr: 'auto' }}
          >
            Supprimer l'inscription
          </Button>
          <Button onClick={closeDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Registrations;